import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Clock,
  Users,
  Power,
  Calendar,
  Save,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { shiftService, UpdateShiftData } from '../../../services/shiftService'
import { ShiftModel } from '../../../types'
import { ConfirmDialog, useConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { extractApiErrors } from '../../../utils/errorHandler'

export function ShiftDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [shift, setShift] = useState<ShiftModel | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<UpdateShiftData>({})

  const confirmDialog = useConfirmDialog()

  useEffect(() => {
    if (id) {
      loadShift()
    }
  }, [id])

  const loadShift = async () => {
    try {
      setLoading(true)
      const data = await shiftService.getById(id!)
      setShift(data)
      setFormData({
        code: data.code,
        name: data.name,
        description: data.description,
        type: data.type,
        isActive: data.isActive,
      })
    } catch (error) {
      toast.error('Error al cargar el turno')
      navigate('/configuration/shifts')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!shift) return
    setSaving(true)

    try {
      await shiftService.update(shift.id, formData)
      toast.success('Turno actualizado')
      setIsEditing(false)
      loadShift()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = () => {
    if (shift) {
      setFormData({
        code: shift.code,
        name: shift.name,
        description: shift.description,
        type: shift.type,
        isActive: shift.isActive,
      })
    }
    setIsEditing(false)
  }

  const handleToggleActive = async () => {
    if (!shift) return

    try {
      await shiftService.update(shift.id, { isActive: !shift.isActive })
      toast.success(`Turno ${shift.isActive ? 'desactivado' : 'activado'}`)
      loadShift()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    }
  }

  const handleDelete = async () => {
    if (!shift) return

    const confirmed = await confirmDialog.confirm({
      title: 'Eliminar turno',
      message: `¿Está seguro de eliminar "${shift.name}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    })

    if (!confirmed) return

    try {
      await shiftService.delete(shift.id)
      toast.success('Turno eliminado')
      navigate('/configuration/shifts')
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    }
  }

  const getBadgeColor = (code: string) => {
    const colorMap: Record<string, string> = {
      A: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      B: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      C: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      D: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      E: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      R3: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
    }
    return colorMap[code] || 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
  }

  if (loading) {
    return (
      <div className="animate-slide-in">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </div>
    )
  }

  if (!shift) {
    return (
      <div className="animate-slide-in">
        <div className="text-center py-12">
          <p className="text-gray-500">Turno no encontrado</p>
          <Link to="/configuration/shifts" className="btn btn-primary mt-4">
            Volver a la lista
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/configuration/shifts"
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-full text-lg font-bold ${getBadgeColor(shift.code)}`}>
                  {shift.code}
                </span>
                <h1 className="page-title">{shift.name}</h1>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className={`badge ${shift.type === '5R' ? 'badge-primary' : 'badge-warning'}`}>
                  {shift.type === '5R' ? '5 Relevos' : '3 Relevos'}
                </span>
                <span className={`badge ${shift.isActive ? 'badge-success' : 'badge-gray'}`}>
                  {shift.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="btn btn-outline flex items-center gap-2"
                  disabled={saving}
                >
                  <X className="h-4 w-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="btn btn-primary flex items-center gap-2"
                  disabled={saving}
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleToggleActive}
                  className={`btn ${shift.isActive ? 'btn-outline' : 'btn-success'} flex items-center gap-2`}
                >
                  <Power className="h-4 w-4" />
                  {shift.isActive ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Editar
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-danger flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Eliminar
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Información del Turno
            </h2>

            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="label">Código del turno *</label>
                  <input
                    type="text"
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="input w-full"
                    placeholder="Ej: A, B, C, R3"
                  />
                </div>

                <div>
                  <label className="label">Nombre del turno *</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input w-full"
                    placeholder="Ej: Turno A, Turno 3R (Plisado)"
                  />
                </div>

                <div>
                  <label className="label">Descripción</label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value || null })}
                    className="input w-full"
                    placeholder="Descripción opcional del turno"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="label">Tipo de turno *</label>
                  <select
                    value={formData.type || '5R'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as '5R' | '3R' })}
                    className="input w-full"
                  >
                    <option value="5R">5 Relevos (5R)</option>
                    <option value="3R">3 Relevos (3R)</option>
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive ?? true}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
                    Turno activo
                  </label>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Código</p>
                    <p className="font-medium text-gray-900 dark:text-white">{shift.code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Nombre</p>
                    <p className="font-medium text-gray-900 dark:text-white">{shift.name}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Descripción</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {shift.description || 'Sin descripción'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Tipo</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {shift.type === '5R' ? '5 Relevos' : '3 Relevos'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Estado</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {shift.isActive ? 'Activo' : 'Inactivo'}
                    </p>
                  </div>
                </div>

                {shift.createdAt && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Creado</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {new Date(shift.createdAt).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      {shift.updatedAt && (
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Última actualización</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {new Date(shift.updatedAt).toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Stats */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              Empleados Asignados
            </h2>
            <div className="text-center py-4">
              <p className="text-4xl font-bold text-primary-600 dark:text-primary-400">
                {shift.employeeCount || 0}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                empleados en este turno
              </p>
            </div>
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Centros Relacionados
            </h2>
            <div className="space-y-2">
              {shift.type === '5R' ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    Nave 1, Nave 2, Nave 3, Nave 4
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    Impresora, Nanopack
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    Calidad, Laboratorio, Otros
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                  Centro de Plisado
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog {...confirmDialog.dialogProps} />
    </div>
  )
}
